import React from 'react';
import useGameStore from '../engine/store';
import { getPersonality } from '../engine/opponents';

export default function CareerScreen() {
  const {
    careerScore, dealsCompleted, dealsWalked, bestStreak,
    totalRoundsPlayed, careerHistory, goToMenu, startNegotiation,
  } = useGameStore();

  const winRate = (dealsCompleted + dealsWalked) > 0
    ? ((dealsCompleted / (dealsCompleted + dealsWalked)) * 100).toFixed(0)
    : 0;

  const avgScore = careerHistory.length > 0
    ? Math.round(careerHistory.reduce((s, h) => s + h.score, 0) / careerHistory.length)
    : 0;

  // Grade distribution
  const grades = {};
  for (const h of careerHistory) {
    grades[h.grade] = (grades[h.grade] || 0) + 1;
  }

  return (
    <div className="min-h-screen bg-dark-900 bg-grid flex items-center justify-center p-4">
      <div className="max-w-2xl w-full animate-slide-up">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">📊</div>
          <h1 className="text-4xl font-black text-white">Career Stats</h1>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Career Score</div>
            <div className="text-2xl font-black font-mono text-neon-yellow">{careerScore.toLocaleString()}</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Deals Won</div>
            <div className="text-2xl font-black font-mono text-neon-green">{dealsCompleted}</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Win Rate</div>
            <div className="text-2xl font-black font-mono text-neon-blue">{winRate}%</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Best Streak</div>
            <div className="text-2xl font-black font-mono text-neon-orange">🔥 {bestStreak}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="glass-panel p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Avg Score</div>
            <div className="text-lg font-bold font-mono text-white">{avgScore}</div>
          </div>
          <div className="glass-panel p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Total Rounds</div>
            <div className="text-lg font-bold font-mono text-white">{totalRoundsPlayed}</div>
          </div>
          <div className="glass-panel p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Deals Walked</div>
            <div className="text-lg font-bold font-mono text-neon-red">{dealsWalked}</div>
          </div>
        </div>

        {/* Grade distribution */}
        {Object.keys(grades).length > 0 && (
          <div className="glass-panel p-4 mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Grade Distribution</div>
            <div className="flex items-end justify-center gap-3 h-24">
              {['S', 'A', 'B', 'C', 'D', 'F'].map(g => {
                const count = grades[g] || 0;
                const maxCount = Math.max(...Object.values(grades), 1);
                const height = (count / maxCount) * 80;
                const colors = { S: '#ffcc00', A: '#00ff88', B: '#00aaff', C: '#ff8800', D: '#ff5500', F: '#ff3366' };
                return (
                  <div key={g} className="flex flex-col items-center gap-1">
                    <span className="text-xs font-mono text-gray-400">{count}</span>
                    <div
                      className="w-8 rounded-t transition-all duration-500"
                      style={{ height: Math.max(4, height), background: colors[g] }}
                    />
                    <span className="text-xs font-bold" style={{ color: colors[g] }}>{g}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History */}
        {careerHistory.length > 0 && (
          <div className="glass-panel p-4 mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Recent Deals</div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {[...careerHistory].reverse().slice(0, 15).map((h, i) => {
                const pData = getPersonality(h.personality);
                const gradeColors = { S: '#ffcc00', A: '#00ff88', B: '#00aaff', C: '#ff8800', D: '#ff5500', F: '#ff3366' };
                return (
                  <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-dark-800/40">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-black font-mono" style={{ color: gradeColors[h.grade] }}>{h.grade}</span>
                      <div>
                        <div className="text-xs text-gray-300">{h.scenario}</div>
                        <div className="text-[10px] text-gray-500">vs {h.opponent} {pData?.emoji}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xs" style={{ color: h.dealReached ? '#00ff88' : '#ff3366' }}>
                        {h.dealReached ? '🤝' : '💨'} {h.score}pts
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <button onClick={() => startNegotiation()} className="btn-primary px-10 py-4">
            🤝 New Deal
          </button>
          <button onClick={goToMenu} className="btn-secondary px-8 py-4">
            ← Menu
          </button>
        </div>
      </div>
    </div>
  );
}
